import useCheckUnsavedChanges from 'core/custom-hooks/use-check-unsaved-changes.hook';
import { useEffect, useRef, useState } from 'react';

import { EapSettingsCreateModel } from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/settings-devices-eap-settings-form/settings-devices-eap-settings-form.model';
import {
    initialEapProtocolSettingsFormValues,
    initialEapSettingsCreateFormModel,
} from './use-eap-settings-data-handler.constants';
import { hasDataChanged, isFormDataValid } from './use-eap-settings-data-handler.validation';

interface UseEapSettingsDataHandlerProps {
    onClose: VoidFunction;
    initialData?: EapSettingsCreateModel;
    isEditMode: boolean;
}

export const useEapSettingsDataHandler = ({ onClose, initialData, isEditMode }: UseEapSettingsDataHandlerProps) => {
    const resolvedInitialData = initialData ?? initialEapSettingsCreateFormModel;
    const [eapSettings, setEapSettings] = useState<EapSettingsCreateModel>(resolvedInitialData);
    const initialDataRef = useRef<EapSettingsCreateModel>(resolvedInitialData);

    useEffect(() => {
        if (initialData) {
            setEapSettings(initialData);
            initialDataRef.current = initialData;
        }
    }, [initialData]);

    const unsavedData = useCheckUnsavedChanges(onClose);

    const handleDataChange = (
        updatedEapSettings: keyof EapSettingsCreateModel,
        value: EapSettingsCreateModel[keyof EapSettingsCreateModel]
    ) => {
        setEapSettings((prevState) => {
            const prev = prevState || ({} as EapSettingsCreateModel);

            // If eapType changes, reset protocol fields using prev (always up-to-date)
            const shouldResetProtocol = updatedEapSettings === 'eapType' && prev.eapType !== value;

            const newState = {
                ...prev,
                ...(shouldResetProtocol ? initialEapProtocolSettingsFormValues : {}),
                [updatedEapSettings]: value,
            };

            // Only mark as unsaved if data actually changed from initial
            const dataChanged = hasDataChanged(newState, initialDataRef.current);
            unsavedData.setIsUnsavedChanges(dataChanged);

            return newState;
        });
    };

    const isDataValid = () => {
        const dataChanged = hasDataChanged(eapSettings, initialDataRef.current);
        return dataChanged && isFormDataValid(eapSettings, isEditMode);
    };

    return { eapSettings, handleDataChange, unsavedData, isDataValid };
};
